import React, { useRef, useEffect, useState } from 'react';

interface MobileTabNavigationProps {
  activeTab: 'agents' | 'users' | 'documents' | 'knowledgeGraph' | 'models';
  onTabChange: (tab: 'agents' | 'users' | 'documents' | 'knowledgeGraph' | 'models') => void;
}

const MobileTabNavigation: React.FC<MobileTabNavigationProps> = ({ activeTab, onTabChange }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check scroll position and update arrow visibility
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Scroll to active tab when it changes
  const scrollToActiveTab = () => {
    if (scrollContainerRef.current) {
      const activeButton = scrollContainerRef.current.querySelector(`[data-tab="${activeTab}"]`) as HTMLElement;
      if (activeButton) {
        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        // Check if button is fully visible
        const isVisible = buttonRect.left >= containerRect.left && buttonRect.right <= containerRect.right;

        if (!isVisible) {
          activeButton.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    }
  };

  // Scroll left/right functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -120,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 120,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    checkScrollPosition();
    scrollToActiveTab();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollToActiveTab/checkScrollPosition read refs, not state; activeTab triggers the re-run
  }, [activeTab]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);

      // Initial check
      setTimeout(checkScrollPosition, 100);

      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, []);

  const tabs = [
    {
      id: 'agents',
      title: 'Agents',
      icon: (
        <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
          />
        </svg>
      )
    },
    {
      id: 'users',
      title: 'Users',
      icon: (
        <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z'
          />
        </svg>
      )
    },
    {
      id: 'documents',
      title: 'Documents',
      icon: (
        <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
          />
        </svg>
      )
    },
    // {
    //   id: 'sharepoint',
    //   title: 'SharePoint',
    //   icon: (
    //     <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
    //       <path
    //         strokeLinecap='round'
    //         strokeLinejoin='round'
    //         strokeWidth={2}
    //         d='M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z'
    //       />
    //     </svg>
    //   )
    // },
    {
      id: 'knowledgeGraph',
      title: 'Knowledge Graph',
      icon: (
        <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7'
          />
        </svg>
      )
    },
    {
      id: 'models',
      title: 'Models',
      icon: (
        <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z'
          />
        </svg>
      )
    },
    // {
    //   id: 'facebook',
    //   title: 'Facebook',
    //   icon: (...),
    // },
    // {
    //   id: 'zalo',
    //   title: 'Zalo',
    //   icon: (...),
    // },
    // {
    //   id: 'teams',
    //   title: 'Teams',
    //   icon: (
    //     <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
    //       <path
    //         strokeLinecap='round'
    //         strokeLinejoin='round'
    //         strokeWidth={2}
    //         d='M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z'
    //       />
    //     </svg>
    //   )
    // },
  ];

  return (
    <div className='mobile-tab-navigation relative border-b border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary lg:hidden'>
      {/* Left scroll arrow */}
      {showLeftArrow && (
        <button
          onClick={scrollLeft}
          className='absolute bottom-0 left-0 top-0 z-10 flex items-center justify-center bg-gradient-to-r from-white to-transparent px-2 dark:from-gray-800 dark:to-transparent'
          aria-label='Scroll left'
        >
          <svg className='h-4 w-4 text-gray-600 dark:text-dark-text-secondary' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
        </button>
      )}

      {/* Right scroll arrow */}
      {showRightArrow && (
        <button
          onClick={scrollRight}
          className='absolute bottom-0 right-0 top-0 z-10 flex items-center justify-center bg-gradient-to-l from-white to-transparent px-2 dark:from-gray-800 dark:to-transparent'
          aria-label='Scroll right'
        >
          <svg className='h-4 w-4 text-gray-600 dark:text-dark-text-secondary' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>
        </button>
      )}

      {/* Scrollable tabs container */}
      <div
        ref={scrollContainerRef}
        className='flex overflow-x-auto scroll-smooth'
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            data-tab={tab.id}
            title={tab.title}
            onClick={() => onTabChange(tab.id as any)}
            className={`flex min-w-[100px] flex-shrink-0 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-b-2 border-primary bg-primary/10 text-primary dark:border-primary dark:bg-primary/20 dark:text-white'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
            }`}
          >
            {tab.icon}
            <span className='whitespace-nowrap'>{tab.title}</span>
          </button>
        ))}
      </div>

      {/* Custom CSS for hiding scrollbar */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
                    .mobile-tab-navigation .flex::-webkit-scrollbar {
                        display: none;
                    }
                `
        }}
      />
    </div>
  );
};

export default MobileTabNavigation;
